import type { ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { useEffect, useRef } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";

import { useAppDispatch } from "@/hooks/redux";

interface Options {
    debounceMs?: number;
    flushOnUnmount?: boolean;
}

export const useSyncRhfWithStore = <TValues extends FieldValues>(
    form: UseFormReturn<TValues>,
    action: ActionCreatorWithPayload<TValues>,
    { debounceMs = 200, flushOnUnmount = true }: Options = {},
) => {
    const dispatch = useAppDispatch();
    const timerReference = useRef<number | null>(null);

    useEffect(() => {
        const sub = form.watch((values) => {
            if (timerReference.current)
                window.clearTimeout(timerReference.current);
            timerReference.current = window.setTimeout(() => {
                dispatch(action(values as TValues));
            }, debounceMs);
        });

        return () => {
            if (timerReference.current) {
                if (flushOnUnmount) {
                    dispatch(action(form.getValues() as TValues));
                }
                window.clearTimeout(timerReference.current);
            }
            sub.unsubscribe();
        };
    }, [dispatch, form, action, debounceMs, flushOnUnmount]);
};
